import React, { useState, useEffect, useRef } from "react";
import { BudgetChart } from "../../../../components/charts/DashboardCharts";
import { getAccountCurrency } from "../../../../utils/systemSettings";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { ExternalLink } from "lucide-react";
import useNavigation from "../../../../utils/useNavigation";

const PortfoliosPanel = ({ portfolios = [], onPortfolioClick, loading }) => {
  const settings = JSON.parse(localStorage.getItem("settings"));
  const [setting] = useState(settings);
  const currency = getAccountCurrency(setting);
  const containerRef = useRef(null);
  const [dummyCount, setDummyCount] = useState(4);
  const [columnWidths, setColumnWidths] = useState([]);
  const { navigateTo } = useNavigation();
  const items = portfolios || [];

  const handleRedirect = (portfolio) => {
    navigateTo("portfolio", portfolio.id, portfolio.name);
  };

  useEffect(() => {
    const calculateColumnWidths = () => {
      if (containerRef.current) {
        const pxToRem = (px) => px / 16;
        const remToPx = (rem) => rem * 16;

        const containerWidth = containerRef.current.offsetWidth;
        const labelWidth = 96;
        const availableWidth = containerWidth - labelWidth;
        const defaultColumnWidth = 15;
        const minColumnWidth = 15;

        if (items.length === 0) {
          setDummyCount(Math.max(1, Math.floor(availableWidth / remToPx(defaultColumnWidth))));
          setColumnWidths([]);
          return;
        }

        const estimatedWidths = items.map((item) => {
          const nameWidth = item?.name ? Math.max(minColumnWidth, pxToRem(item.name.length * 7)) : defaultColumnWidth;
          return nameWidth;
        });

        const totalNeededWidth = estimatedWidths.reduce((sum, width) => sum + remToPx(width), 0);

        if (totalNeededWidth <= availableWidth) {
          setColumnWidths(estimatedWidths.map((width) => `${width}rem`));
        } else {
          const extraWidthNeeded = totalNeededWidth - availableWidth;
          const sortedIndices = estimatedWidths
            .map((width, index) => ({ width, index }))
            .sort((a, b) => b.width - a.width);

          const adjustedWidths = [...estimatedWidths];
          let remainingWidthToReduce = pxToRem(extraWidthNeeded);
          let i = 0;

          while (remainingWidthToReduce > 0 && i < sortedIndices.length) {
            const { width, index } = sortedIndices[i];
            const maxReduction = Math.max(0, width - minColumnWidth);
            const reduction = Math.min(maxReduction, remainingWidthToReduce);
            adjustedWidths[index] -= reduction;
            remainingWidthToReduce -= reduction;
            i++;
            if (i === sortedIndices.length && remainingWidthToReduce > 0) {
              adjustedWidths.forEach((_, idx) => { adjustedWidths[idx] = minColumnWidth; });
              remainingWidthToReduce = 0;
            }
          }

          setColumnWidths(adjustedWidths.map((width) => `${Math.max(minColumnWidth, width)}rem`));
        }

        setDummyCount(Math.max(1, Math.floor(availableWidth / remToPx(defaultColumnWidth))));
      }
    };

    calculateColumnWidths();
    window.addEventListener("resize", calculateColumnWidths);
    return () => window.removeEventListener("resize", calculateColumnWidths);
  }, [items.length]);

  const categories = [
    {
      key: "cost",
      label: "Budget",
      render: (portfolio) => (
        <div className="flex justify-center">
          <BudgetChart
            budgetSummary={portfolio.costs}
            currencySymbol={currency}
          />
        </div>
      ),
    },
  ];

  const tdStyle = {
    minWidth: "15rem",
    maxWidth: "15rem",
    width: "15rem",
    padding: "1rem 0.5rem",
  };

  const renderLoadingSkeleton = () => {
    const dummyItems = Array(dummyCount).fill(0);
    return (
      <div className="p-3">
        <table className="w-full border-collapse" style={{ minWidth: "1200px" }}>
          <thead>
            <tr className="border-b">
              <th className="text-left p-3 overview-header-title sticky left-0 bg-white"
                style={{ minWidth: "6rem", maxWidth: "6rem", width: "6rem" }}>
                <Skeleton width={80} />
              </th>
              {dummyItems.map((_, index) => (
                <th key={index} className="text-center p-3 overview-header" style={tdStyle}>
                  <Skeleton width={160} />
                </th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {categories.map((category, index) => (
              <tr key={category.key} className={index < categories.length - 1 ? "border-b" : ""}>
                <td className="text-left p-3 overview-label sticky left-0 bg-white"
                  style={{ minWidth: "6rem", maxWidth: "6rem", width: "6rem" }}>
                  {category.label}
                </td>
                {dummyItems.map((_, i) => (
                  <td key={i} style={tdStyle}>
                    <Skeleton height={40} width={140} style={{ borderRadius: "10px" }} />
                  </td>
                ))}
                <td></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (!loading && !items.length) return null;

  return (
    <div className="DashboardCard overview-table w-full pt-0" ref={containerRef}>
      {loading ? (
        renderLoadingSkeleton()
      ) : (
        <div className="p-3 pt-0">
          <table className="w-full border-collapse" style={{ minWidth: "1200px" }}>
            <thead>
              <tr className="border-b">
                <th className="text-left p-3 overview-header-title sticky left-0 bg-white"
                  style={{ minWidth: "6rem", maxWidth: "6rem", width: "6rem" }}></th>
                {items.map((portfolio, index) => (
                  <th
                    key={portfolio.id}
                    className="text-center p-3 overview-header cursor-pointer"
                    style={{
                      minWidth: columnWidths[index] || "15rem",
                      maxWidth: columnWidths[index] || "15rem",
                      width: columnWidths[index] || "15rem",
                      padding: "1rem 0.5rem",
                    }}
                    onClick={() => onPortfolioClick?.(portfolio)}
                  >
                    <div className="d-flex flex-col gap-1 align-items-center justify-center">
                      {portfolio.image && (
                        <div className="flex flex-col items-center justify-center min-w-10 max-w-30 h-7 overflow-hidden mr-1">
                          <img src={portfolio.image} className="max-w-full max-h-full object-contain" />
                        </div>
                      )}
                      <div className="flex gap-1 items-center justify-center">
                        <p className="text-truncate w-100">{portfolio.name}</p>
                        <ExternalLink
                          className="overview-header-name-link"
                          size={16}
                          color="#342C82"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRedirect(portfolio);
                          }}
                        />
                      </div>
                    </div>
                  </th>
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category, index) => (
                <tr key={category.key} className={index < categories.length - 1 ? "border-b" : ""}>
                  <td className="text-left p-3 overview-label sticky left-0 bg-white"
                    style={{
                      minWidth: "6rem",
                      maxWidth: "6rem",
                      width: "6rem",
                    }}>
                    {category.label}
                  </td>
                  {items.map((portfolio, index) => (
                    <td
                      key={portfolio.id}
                      style={{
                        minWidth: columnWidths[index] || "15rem",
                        maxWidth: columnWidths[index] || "15rem",
                        width: columnWidths[index] || "15rem",
                        padding: "1rem 0.5rem",
                      }}
                    >
                      {category.render(portfolio)}
                    </td>
                  ))}
                  <td></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PortfoliosPanel;
